/**
 * RC-152c-2a + RC-152c-2a-style — Form Designer · per-field
 * inspector (right column).
 *
 * Renders the property controls for the currently-selected field in
 * the FormCanvas. Always shows the four universal properties
 * (name / label / required / helpText) and then iterates the
 * type-specific properties from {@link FIELD_PROPS} so adding a new
 * property to a type requires zero changes here — just extend the
 * registry.
 *
 * <p><b>Style contract (RC-152c-2a-style operator directive,
 * 2026-06-21):</b> EVERY control is a V2 design-system primitive
 * (FieldGroup wrapping Input/Textarea, Button, Label). NO inline
 * {@code style={{}}} objects with hardcoded padding/colors/borders.
 * Layout-only Tailwind utilities (flex/grid/gap) are allowed.</p>
 *
 * <p><b>Control kinds in the registry:</b></p>
 * <ul>
 *   <li><b>text</b> — single-line input.</li>
 *   <li><b>number</b> — numeric input.</li>
 *   <li><b>textarea</b> — multi-line input.</li>
 *   <li><b>options</b> — repeating {value, label} pair editor for
 *       select / multi-select types.</li>
 *   <li><b>string-array</b> — comma-separated tokens (e.g.
 *       acceptedTypes on file fields).</li>
 * </ul>
 *
 * <p><b>Empty state:</b> when no field is selected, renders a hint
 * telling the operator to click a field on the canvas.</p>
 */

import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input, Textarea, Select, FieldGroup, Label, Button } from '../../components/v2';
import { FIELD_PROPS } from './_shared/fieldTypes';

export default function FieldInspector({
  field,
  fieldIndex,
  onUpdateField,
  readOnly = false,
}) {
  // ── Empty state ──────────────────────────────────────────────────
  if (!field || fieldIndex == null) {
    return (
      <aside
        aria-label="Field properties"
        className="p-4 border-l border-v2-border-subtle bg-v2-muted"
      >
        <div className="text-[12px] text-v2-ink-muted leading-relaxed">
          Select a field on the canvas to edit its properties.
        </div>
      </aside>
    );
  }

  const fieldType = (field.type || 'text').toLowerCase();
  const typeProps = FIELD_PROPS[fieldType] || [];

  /**
   * Per-property change handler. Folds the new value into the
   * field via the parent's reducer-dispatched updater. Stripping
   * empty-string values keeps the persisted schema lean
   * ({@code {maxLength: ""}} becomes {@code {maxLength: undefined}}
   * which the runtime treats as absent).
   */
  const setProp = (key, value) => {
    if (readOnly) return;
    const next = { ...field };
    if (value === '' || value === null || value === undefined) {
      delete next[key];
    } else {
      next[key] = value;
    }
    onUpdateField(fieldIndex, next);
  };

  /**
   * Number-control change handler. Parses to Number when valid,
   * preserves the raw string otherwise so the operator can finish
   * typing without losing keystrokes.
   */
  const setNumber = (key, raw) => {
    if (readOnly) return;
    if (raw === '' || raw == null) { setProp(key, undefined); return; }
    const n = Number(raw);
    setProp(key, Number.isFinite(n) ? n : raw);
  };

  return (
    <aside
      aria-label="Field properties"
      className="p-4 border-l border-v2-border-subtle bg-v2-muted overflow-y-auto flex flex-col gap-3"
    >
      <div>
        <div className="text-[10px] text-v2-ink-muted uppercase tracking-wide font-semibold">
          Field properties · {fieldType}
        </div>
        <div className="text-[13px] font-semibold text-v2-ink mt-0.5">
          {field.label || '(unlabeled)'}
        </div>
      </div>

      {/* ── Universal properties ────────────────────────────────── */}
      <FieldGroup
        label="Field name (downstream key)"
        hint="Downstream nodes reference this field as ${nodeId.output.name}. No spaces — use snake_case."
      >
        <Input
          size="sm"
          value={field.name || ''}
          onChange={(e) => setProp('name', e.target.value)}
          disabled={readOnly}
        />
      </FieldGroup>

      <FieldGroup
        label="Label"
        hint="Operator-facing label shown above the input in the Approval Center."
      >
        <Input
          size="sm"
          value={field.label || ''}
          onChange={(e) => setProp('label', e.target.value)}
          disabled={readOnly}
        />
      </FieldGroup>

      <label className={
        'flex items-center gap-2 text-[12px] text-v2-ink ' +
        (readOnly ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')
      }>
        <input
          type="checkbox"
          checked={!!field.required}
          disabled={readOnly}
          onChange={(e) => setProp('required', e.target.checked)}
        />
        Required
      </label>

      <FieldGroup
        label="Help text"
        hint="Optional one-line hint rendered under the input."
      >
        <Input
          size="sm"
          value={field.helpText || ''}
          onChange={(e) => setProp('helpText', e.target.value)}
          disabled={readOnly}
        />
      </FieldGroup>

      {/* ── Per-type properties from the registry ───────────────── */}
      {typeProps.length > 0 && (
        <>
          <hr className="my-1 border-0 border-t border-v2-border-subtle" />
          <div className="text-[10px] text-v2-ink-muted uppercase tracking-wide font-semibold">
            Type-specific properties
          </div>
          {typeProps.map((p) => {
            if (p.control === 'text') {
              return (
                <FieldGroup key={p.key} label={p.label} hint={p.helpText}>
                  <Input
                    size="sm"
                    value={field[p.key] ?? ''}
                    onChange={(e) => setProp(p.key, e.target.value)}
                    disabled={readOnly}
                    placeholder={p.placeholder}
                  />
                </FieldGroup>
              );
            }
            if (p.control === 'textarea') {
              return (
                <FieldGroup key={p.key} label={p.label} hint={p.helpText}>
                  <Textarea
                    size="sm"
                    value={field[p.key] ?? ''}
                    onChange={(e) => setProp(p.key, e.target.value)}
                    disabled={readOnly}
                    rows={p.rows || 3}
                  />
                </FieldGroup>
              );
            }
            if (p.control === 'number') {
              return (
                <FieldGroup key={p.key} label={p.label} hint={p.helpText}>
                  <Input
                    size="sm"
                    type="number"
                    value={field[p.key] ?? ''}
                    onChange={(e) => setNumber(p.key, e.target.value)}
                    disabled={readOnly}
                  />
                </FieldGroup>
              );
            }
            if (p.control === 'date-bound') {
              return (
                <DateBoundEditor
                  key={p.key}
                  label={p.label}
                  helpText={p.helpText}
                  value={field[p.key] ?? ''}
                  onChange={(v) => setProp(p.key, v)}
                  readOnly={readOnly}
                />
              );
            }
            if (p.control === 'datetime-bound') {
              return (
                <DatetimeBoundEditor
                  key={p.key}
                  label={p.label}
                  helpText={p.helpText}
                  value={field[p.key] ?? ''}
                  onChange={(v) => setProp(p.key, v)}
                  readOnly={readOnly}
                />
              );
            }
            if (p.control === 'options') {
              return (
                <OptionsEditor
                  key={p.key}
                  label={p.label}
                  options={Array.isArray(field[p.key]) ? field[p.key] : []}
                  onChange={(next) => setProp(p.key, next)}
                  readOnly={readOnly}
                  helpText={p.helpText}
                />
              );
            }
            if (p.control === 'string-array') {
              return (
                <StringArrayEditor
                  key={p.key}
                  label={p.label}
                  value={Array.isArray(field[p.key]) ? field[p.key] : []}
                  onChange={(next) => setProp(p.key, next)}
                  readOnly={readOnly}
                  placeholder={p.placeholder}
                  helpText={p.helpText}
                />
              );
            }
            return null;
          })}
        </>
      )}
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────────
// Sub-editors — use V2 primitives, layout-only Tailwind.
// ──────────────────────────────────────────────────────────────────

/**
 * Editor for select / multi-select options. Each option is an
 * {value, label} pair. Adding/removing/reordering happens here;
 * upstream just persists the array.
 */
function OptionsEditor({ label, options, onChange, readOnly, helpText }) {
  const setAt = (i, key, val) => {
    if (readOnly) return;
    const next = options.slice();
    next[i] = { ...(next[i] || {}), [key]: val };
    onChange(next);
  };
  const remove = (i) => {
    if (readOnly) return;
    const next = options.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const append = () => {
    if (readOnly) return;
    onChange([...options, { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` }]);
  };
  return (
    <div>
      <Label>{label}</Label>
      {helpText && (
        <div className="text-[11px] text-v2-ink-muted mb-1.5 leading-snug">{helpText}</div>
      )}
      <div className="flex flex-col gap-1.5">
        {options.length === 0 && (
          <div className="text-[11px] text-v2-ink-muted italic">
            No options yet — click + Option to add the first one.
          </div>
        )}
        {options.map((opt, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-1">
            <Input
              size="sm"
              value={opt.value ?? ''}
              onChange={(e) => setAt(i, 'value', e.target.value)}
              placeholder="value"
              disabled={readOnly}
              className="font-mono"
            />
            <Input
              size="sm"
              value={opt.label ?? ''}
              onChange={(e) => setAt(i, 'label', e.target.value)}
              placeholder="label"
              disabled={readOnly}
            />
            <Button
              variant="ghost"
              size="xs"
              onClick={() => remove(i)}
              disabled={readOnly}
              ariaLabel="Remove option"
              iconLeft={<Trash2 />}
            >
              <span className="sr-only">Remove</span>
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-1.5">
        <Button
          variant="secondary"
          size="xs"
          iconLeft={<Plus />}
          onClick={append}
          disabled={readOnly}
        >
          Option
        </Button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Date / Datetime bound editor — dual-mode (Absolute calendar+TZ vs
// Relative expression). RC-152c-2a-datetz.
// ──────────────────────────────────────────────────────────────────

/**
 * Is the given string a relative-datetime expression rather than an
 * absolute ISO date(time)? Conservative match — anything starting
 * with 'now' (with optional ±offset), or anything that starts with
 * '+' or '-' is treated as relative; anything that looks like an
 * ISO date / datetime is treated as absolute; empty / unknown is
 * absolute (the default authoring mode).
 *
 * Examples treated as relative: "now", "now+1h", "now-1d",
 *   "now+30m", "+15m", "-2h".
 * Examples treated as absolute: "2026-12-31", "2026-12-31T23:59",
 *   "2026-12-31T23:59:00-05:00", "" (empty).
 */
function isRelativeExpression(v) {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;
  if (/^now\b/i.test(s)) return true;
  if (/^[+\-]\d/.test(s)) return true;
  return false;
}

/**
 * Curated list of named IANA timezones for the picker. We don't
 * use {@code Intl.supportedValuesOf('timeZone')} because (a) the
 * full list is 400+ entries which is operator-hostile, and (b) it
 * isn't supported in every browser we ship to. The list below
 * covers the operationally common zones; the 'browser' entry maps
 * to {@code Intl.DateTimeFormat().resolvedOptions().timeZone} at
 * runtime so workflows still respect viewer-local time.
 */
const TZ_OPTIONS = [
  { value: 'browser',         label: 'Viewer local (browser timezone)' },
  { value: 'UTC',             label: 'UTC' },
  { value: 'America/New_York',     label: 'America / New_York (Eastern)' },
  { value: 'America/Chicago',      label: 'America / Chicago (Central)' },
  { value: 'America/Denver',       label: 'America / Denver (Mountain)' },
  { value: 'America/Los_Angeles',  label: 'America / Los_Angeles (Pacific)' },
  { value: 'Europe/London',        label: 'Europe / London' },
  { value: 'Europe/Berlin',        label: 'Europe / Berlin' },
  { value: 'Europe/Paris',         label: 'Europe / Paris' },
  { value: 'Asia/Dubai',           label: 'Asia / Dubai' },
  { value: 'Asia/Kolkata',         label: 'Asia / Kolkata (India)' },
  { value: 'Asia/Singapore',       label: 'Asia / Singapore' },
  { value: 'Asia/Tokyo',           label: 'Asia / Tokyo' },
  { value: 'Australia/Sydney',     label: 'Australia / Sydney' },
];

/**
 * Browser's resolved IANA timezone — used as the picker's initial
 * timezone and as the resolver for the 'browser' option.
 */
function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Compute the numeric UTC offset (in minutes) for a given IANA
 * timezone at a given instant. Used to convert the picker's
 * "local-wall-clock-in-TZ" reading back to a UTC-anchored ISO with
 * offset suffix. Returns 0 (UTC) on failure rather than throwing —
 * the cost of a wrong offset on edge inputs is small and visible.
 */
function tzOffsetMinutes(tz, atDate) {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const parts = dtf.formatToParts(atDate).reduce((acc, p) => {
      if (p.type !== 'literal') acc[p.type] = p.value;
      return acc;
    }, {});
    const asUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second),
    );
    return Math.round((asUtc - atDate.getTime()) / 60000);
  } catch {
    return 0;
  }
}

/** Format an offset-minute integer as "+HH:MM" or "-HH:MM". */
function fmtOffset(min) {
  const sign = min >= 0 ? '+' : '-';
  const abs = Math.abs(min);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/**
 * Parse an ISO-ish absolute string into picker pieces.
 * Tolerant: handles
 *   "2026-12-31"
 *   "2026-12-31T23:59"
 *   "2026-12-31T23:59:00"
 *   "2026-12-31T23:59:00-05:00"
 *   "2026-12-31T23:59:00Z"
 * Returns { date:'YYYY-MM-DD', time:'HH:MM' | '', offset:'+HH:MM' | 'Z' | null }.
 * Returns null on parse failure.
 */
function parseAbsolute(v) {
  if (!v) return { date: '', time: '', offset: null };
  const s = String(v).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2})(?::(\d{2}))?(Z|[+\-]\d{2}:?\d{2})?)?$/);
  if (!m) return null;
  return {
    date: m[1] || '',
    time: m[2] || '',
    offset: m[4] || null,
  };
}

/**
 * Compose a wall-clock {date,time} (interpreted in the given
 * timezone) into an ISO-8601 string with offset suffix.
 *
 * "2026-12-31" + "23:59" in "America/New_York" →
 * "2026-12-31T23:59:00-05:00".
 *
 * If onlyDate is true, returns "YYYY-MM-DD" (no time, no offset)
 * — date-bound editor mode.
 */
function composeAbsolute({ date, time, tz, onlyDate }) {
  if (!date) return '';
  if (onlyDate) return date;
  const t = time || '00:00';
  if (!tz || tz === 'browser') {
    // Naive ISO without offset — runtime treats as viewer-local.
    return `${date}T${t}`;
  }
  const resolvedTz = tz === 'browser' ? browserTimeZone() : tz;
  // Take the wall-clock as if it were UTC, then ask: what is the
  // offset of resolvedTz at that instant?
  const naiveUtc = new Date(`${date}T${t}:00Z`);
  const offsetMin = tzOffsetMinutes(resolvedTz, naiveUtc);
  return `${date}T${t}:00${fmtOffset(offsetMin)}`;
}

/**
 * Recover an IANA timezone label from an ISO offset suffix. Used
 * to repopulate the timezone <Select> when reopening a saved value.
 * Imperfect — offsets aren't 1:1 to zones — so we fall back to
 * 'browser' on ambiguity. The operator can re-pick if needed.
 */
function tzFromOffset(offset) {
  if (!offset) return 'browser';
  if (offset === 'Z') return 'UTC';
  // Walk TZ_OPTIONS for any that currently produce this offset.
  const now = new Date();
  for (const opt of TZ_OPTIONS) {
    if (opt.value === 'browser') continue;
    const min = tzOffsetMinutes(opt.value, now);
    if (fmtOffset(min) === offset) return opt.value;
  }
  return 'browser';
}

/**
 * Mode toggle button row, shared by date-bound + datetime-bound
 * editors. Tiny inline component to avoid two near-identical
 * blocks below.
 */
function BoundModeToggle({ mode, onChange, readOnly }) {
  return (
    <div role="group" aria-label="Bound mode" className="inline-flex gap-1">
      <Button
        variant={mode === 'absolute' ? 'primary' : 'secondary'}
        size="xs"
        onClick={() => onChange('absolute')}
        disabled={readOnly}
      >
        Absolute
      </Button>
      <Button
        variant={mode === 'relative' ? 'primary' : 'secondary'}
        size="xs"
        onClick={() => onChange('relative')}
        disabled={readOnly}
      >
        Relative
      </Button>
    </div>
  );
}

/**
 * date-bound editor — Absolute (HTML5 calendar picker) | Relative
 * (free-text expression). No time, no timezone.
 */
function DateBoundEditor({ label, helpText, value, onChange, readOnly }) {
  const [mode, setMode] = React.useState(isRelativeExpression(value) ? 'relative' : 'absolute');
  // Switching modes does NOT clear the value — operators can flip
  // back without losing their work.
  const parsed = mode === 'absolute' ? (parseAbsolute(value) || { date: '' }) : { date: '' };
  return (
    <FieldGroup label={label} hint={helpText}>
      <div className="flex flex-col gap-1.5">
        <BoundModeToggle mode={mode} onChange={setMode} readOnly={readOnly} />
        {mode === 'absolute' && (
          <Input
            size="sm"
            type="date"
            value={parsed.date}
            onChange={(e) => onChange(composeAbsolute({ date: e.target.value, onlyDate: true }))}
            disabled={readOnly}
          />
        )}
        {mode === 'relative' && (
          <Input
            size="sm"
            value={isRelativeExpression(value) ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            placeholder="now, now+7d, now-1d"
          />
        )}
      </div>
    </FieldGroup>
  );
}

/**
 * datetime-bound editor — Absolute (calendar + time + IANA
 * timezone Select; composes to ISO with offset) | Relative
 * (free-text 'now+1h' expression).
 */
function DatetimeBoundEditor({ label, helpText, value, onChange, readOnly }) {
  const [mode, setMode] = React.useState(isRelativeExpression(value) ? 'relative' : 'absolute');
  const parsed = mode === 'absolute' ? (parseAbsolute(value) || { date: '', time: '', offset: null }) : { date: '', time: '', offset: null };
  const [tz, setTz] = React.useState(tzFromOffset(parsed.offset));

  const emit = (next) => {
    onChange(composeAbsolute({
      date: next.date ?? parsed.date,
      time: next.time ?? parsed.time,
      tz:   next.tz   ?? tz,
    }));
  };

  return (
    <FieldGroup label={label} hint={helpText}>
      <div className="flex flex-col gap-1.5">
        <BoundModeToggle mode={mode} onChange={setMode} readOnly={readOnly} />
        {mode === 'absolute' && (
          <>
            <div className="grid grid-cols-[1fr_auto] gap-1.5">
              <Input
                size="sm"
                type="date"
                value={parsed.date}
                onChange={(e) => emit({ date: e.target.value })}
                disabled={readOnly}
                ariaLabel="Date"
              />
              <Input
                size="sm"
                type="time"
                value={parsed.time}
                onChange={(e) => emit({ time: e.target.value })}
                disabled={readOnly}
                ariaLabel="Time"
              />
            </div>
            <Select
              size="sm"
              value={tz}
              onChange={(e) => { setTz(e.target.value); emit({ tz: e.target.value }); }}
              disabled={readOnly}
              aria-label="Timezone"
            >
              {TZ_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </>
        )}
        {mode === 'relative' && (
          <Input
            size="sm"
            value={isRelativeExpression(value) ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={readOnly}
            placeholder="now, now+1h, now-30m, now+1d"
          />
        )}
      </div>
    </FieldGroup>
  );
}

/**
 * Comma-separated token editor — used for acceptedTypes on file
 * fields. Strips whitespace on parse so "pdf, jpg, png" and
 * "pdf,jpg,png" both work the same.
 */
function StringArrayEditor({ label, value, onChange, readOnly, placeholder, helpText }) {
  const text = value.join(', ');
  return (
    <FieldGroup label={label} hint={helpText}>
      <Input
        size="sm"
        value={text}
        onChange={(e) => {
          if (readOnly) return;
          const parts = e.target.value
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          onChange(parts);
        }}
        disabled={readOnly}
        placeholder={placeholder}
      />
    </FieldGroup>
  );
}
